import "@/App.css";
import { lazy, Suspense, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import DefaultSEO, { PageSeoProvider } from "@/components/seo/DefaultSEO";
import AISearchTelemetry from "@/components/AISearchTelemetry";
import FrenchSuggestionBanner from "@/components/FrenchSuggestionBanner";
import ContactModal from "@/components/ContactModal";
import HomePage from "@/pages/HomePage";
import { CartProvider } from "@/context/CartContext";
import { ShopCartProvider } from "@/context/ShopCartContext";
import AdminAuthGate from "@/components/AdminAuthGate";
import MDAuthGate from "@/components/MDAuthGate";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import SplashScreen from "@/components/SplashScreen";
import RewardsPrompt from "@/components/RewardsPrompt";
import CodeProtection from "@/components/CodeProtection";
import ContentProtection from "@/components/ContentProtection";
import { LanguageProvider } from "@/context/LanguageContext";
import BackToMainButton from "@/components/BackToMainButton";
import Breadcrumb from "@/components/Breadcrumb";
// KarineAudioFloatingWidget removed from global render (Jun 23 2026) — bubble was overlapping the booking cards. Component still lives at /app/frontend/src/components/KarineAudioGreeting.jsx for pages that embed it inline (e.g., About page).
import LiveChat from "@/components/LiveChat";
import KarineAIChatBubble from "@/components/KarineAIChatBubble";
// import FloatingReferral from "@/components/FloatingReferral"; // removed from home Oct 26 2026
import PointsToast from "@/components/PointsToast";
import LumiFAB from "@/components/LumiFAB";
import { LASER_249_BOOK_URL } from "@/lib/laser249";
import BookingCart from "@/components/BookingCart";
import FloatingCartButton from "@/components/FloatingCartButton";
import FloatingTrending from "@/components/FloatingTrending";
import FloatingPoints from "@/components/FloatingPoints";
import SitewidePromoBanner from "@/components/SitewidePromoBanner";
import FallPromoBanner from "@/components/FallPromoBanner";
import WinterPromoBanner from "@/components/WinterPromoBanner";
import VersionBadge from "@/components/VersionBadge";
import PersistentLocationPill from "@/components/PersistentLocationPill";
import StaffPingAlert from "@/components/admin/StaffPingAlert";
import NotSureWizard from "@/components/NotSureWizard";

// ---------------------------------------------------------------------------
// CODE SPLITTING — every non-homepage route loads as its own chunk on demand.
// This shrinks the initial JS bundle dramatically (100+ pages used to ship in
// one file), making first paint + staff sign-in feel instant on clinic tablets.
// ---------------------------------------------------------------------------
const LastMinuteClubPage = lazy(() => import("@/pages/LastMinuteClubPage"));
const AftercarePage = lazy(() => import("@/pages/AftercarePage"));
const InsiderTipsBlogPage = lazy(() => import("@/pages/InsiderTipsBlogPage"));
const InsiderTipsBlogDetailPage = lazy(() => import("@/pages/InsiderTipsBlogDetailPage"));
const GiftCardsPage = lazy(() => import("@/pages/GiftCardsPage"));
const FinancingPage = lazy(() => import("@/pages/FinancingPage"));
const PromotionsPage = lazy(() => import("@/pages/PromotionsPage"));
const TreatmentsIndexPage = lazy(() => import("@/pages/TreatmentsIndexPage"));
const TreatmentsByProviderPage = lazy(() => import("@/pages/TreatmentsByProviderPage"));
const OurServicesPage = lazy(() => import("@/pages/OurServicesPage"));
const ConcernPage = lazy(() => import("@/pages/ConcernPage"));
const TreatmentsByConcernPage = lazy(() => import("@/pages/TreatmentsByConcernPage"));
const AfterpayPage = lazy(() => import("@/pages/AfterpayPage"));
const ConsentFormPage = lazy(() => import("@/pages/ConsentFormPage"));
const OurTeamPage = lazy(() => import("@/pages/OurTeamPage"));
// AvedaFacialsPage retired Feb 2026 · Karine ask — Éminence is now the
// primary product house on the facials & body menu. Old routes redirect
// to /treatments/eminence-facials. File left on disk in case of revert.
const EminenceFacialsPage = lazy(() => import("@/pages/EminenceFacialsPage"));
const EminenceLaunchPreviewPage = lazy(() => import("@/pages/EminenceLaunchPreviewPage"));
const InjectablesPage = lazy(() => import("@/pages/InjectablesPage"));
const VipMembershipsPage = lazy(() => import("@/pages/VipMembershipsPage"));
const BeforeAfterGalleryPage = lazy(() => import("@/pages/BeforeAfterGalleryPage"));
const AdminReferralsPage = lazy(() => import("@/pages/AdminReferralsPage"));
const AdminProReferralsPage = lazy(() => import("@/pages/AdminProReferralsPage"));
const AdminSuitesPage = lazy(() => import("@/pages/AdminSuitesPage"));
const AdminAddOnsPage = lazy(() => import("@/pages/AdminAddOnsPage"));
const AdminWeeklyPromoPage = lazy(() => import("@/pages/AdminWeeklyPromoPage"));
const AdminSpinWinnersPage = lazy(() => import("@/pages/AdminSpinWinnersPage"));
const AdminSpinCatalogPage = lazy(() => import("@/pages/AdminSpinCatalogPage"));
const AdminSpinPrizeDigestPage = lazy(() => import("@/pages/AdminSpinPrizeDigestPage"));
const StaffSpinRedeemPage = lazy(() => import("@/pages/StaffSpinRedeemPage"));
const StaffOnlineOrdersPage = lazy(() => import("@/pages/StaffOnlineOrdersPage"));
const ShareCodePage = lazy(() => import("@/pages/ShareCodePage"));
const AdminFastTrackWatchersPage = lazy(() => import("@/pages/AdminFastTrackWatchersPage"));
const AdminBookingHealthPage = lazy(() => import("@/pages/AdminBookingHealthPage"));
const AdminRescueQueuePage = lazy(() => import("@/pages/AdminRescueQueuePage"));
const AdminRescueListPage = lazy(() => import("@/pages/AdminRescueListPage"));
const AdminMetricsStudioPage = lazy(() => import("@/pages/AdminMetricsStudioPage"));
const AdminProductUsagePage = lazy(() => import("@/pages/AdminProductUsagePage"));
const AdminContractorPayPage = lazy(() => import("@/pages/AdminContractorPayPage"));
const StaffMyPayPage = lazy(() => import("@/pages/StaffMyPayPage"));
const AdminNPAuditPage = lazy(() => import("@/pages/AdminNPAuditPage"));
const AdminLoyaltySkipLogPage = lazy(() => import("@/pages/AdminLoyaltySkipLogPage"));
const AdminBookingsPage = lazy(() => import("@/pages/AdminBookingsPage"));
const AdminGiftCardsPage = lazy(() => import("@/pages/AdminGiftCardsPage"));
const AdminSubscriptionsPage = lazy(() => import("@/pages/AdminSubscriptionsPage"));
const AdminConsultationHistoryPage = lazy(() => import("@/pages/admin/AdminConsultationHistory"));
const AnalyticsDashboardPage = lazy(() => import("@/pages/AnalyticsDashboardPage"));
const BotoxOttawaPage = lazy(() => import("@/pages/treatments/BotoxOttawaPage"));
const CombosPage = lazy(() => import("@/pages/treatments/CombosPage"));
const LiftRestoreProgramPage = lazy(() => import("@/pages/programs/LiftRestoreProgramPage"));
const MenopauseProgramPage = lazy(() => import("@/pages/programs/MenopauseProgramPage"));
const AndropauseProgramPage = lazy(() => import("@/pages/programs/AndropauseProgramPage"));
const PreventionPage = lazy(() => import("@/pages/PreventionPage"));
const TermsOfUsePage = lazy(() => import("@/pages/TermsOfUsePage"));
const LipFillerOttawaPage = lazy(() => import("@/pages/treatments/LipFillerOttawaPage"));
const MicroneedlingOttawaPage = lazy(() => import("@/pages/treatments/MicroneedlingOttawaPage"));
const DermalFillerOttawaPage = lazy(() => import("@/pages/treatments/DermalFillerOttawaPage"));
const DermalFillersPage = lazy(() => import("@/pages/DermalFillersPage"));
const RadiessePage = lazy(() => import("@/pages/RadiessePage"));
// Sept 9 2026 · Karine ask · area-focused SEO landing pages so each
// treatment area ranks for its own Google search intent.
const RadiesseHandsPage = lazy(() => import("@/pages/RadiesseHandsPage"));
const RadiesseNeckDecolletePage = lazy(() => import("@/pages/RadiesseNeckDecolletePage"));
// Sept 4 2026 · Karine ask · one-page "everything we offer, at a glance"
const TreatmentMapPage = lazy(() => import("@/pages/TreatmentMapPage"));
// Sept 4 2026 · Patricia Russell bug fix — public prize-redemption
// landing so anyone with a Spin & Win code can jump straight to
// booking without logging into their Crown Reserve dashboard.
const SpinRedeemLandingPage = lazy(() => import("@/pages/SpinRedeemLandingPage"));
// PREVIEW ONLY · New area-based pricing (Iter 368) — NOT linked from Navbar yet.
// Awaiting Karine's green light before promoting to /dermal-fillers.
const DermalFillersAreaPricingPage = lazy(() => import("@/pages/DermalFillersAreaPricingPage"));
// PREVIEW · SEE YOUR FUTURE™ Ageless-style facial vision AI (Iter 370). Not linked.
const SeeYourFuturePage = lazy(() => import("@/pages/SeeYourFuturePage"));
const LaserAndSkinPage = lazy(() => import("@/pages/LaserAndSkinPage"));
const LashBrowStudioPage = lazy(() => import("@/pages/LashBrowStudioPage"));
const ChemicalPeelsPage = lazy(() => import("@/pages/ChemicalPeelsPage"));
const NavMockupsPage = lazy(() => import("@/pages/NavMockupsPage"));
const PRPOttawaPage = lazy(() => import("@/pages/treatments/PRPOttawaPage"));
const SculptraOttawaPage = lazy(() => import("@/pages/treatments/SculptraOttawaPage"));
const NefertitiLiftOttawaPage = lazy(() => import("@/pages/treatments/NefertitiLiftOttawaPage"));
const RedensityOttawaPage = lazy(() => import("@/pages/treatments/RedensityOttawaPage"));
const CO2LiftOttawaPage = lazy(() => import("@/pages/treatments/CO2LiftOttawaPage"));
const LatisseOttawaPage = lazy(() => import("@/pages/treatments/LatisseOttawaPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
// Eminence retail store (Iter 387 · Feb 15 2026). /shop hidden from nav until Karine publishes products.
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ShopProductPage = lazy(() => import("@/pages/ShopProductPage"));
const ShopOrderCompletePage = lazy(() => import("@/pages/ShopOrderCompletePage"));
const AdminStorePage = lazy(() => import("@/pages/AdminStorePage"));
const Skincare101Page = lazy(() => import("@/pages/Skincare101Page"));
const Skincare101ArticlePage = lazy(() => import("@/pages/Skincare101ArticlePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const VirtualConsultationFAQ = lazy(() => import("@/pages/VirtualConsultationFAQ"));
const VirtualConsultationGuidePage = lazy(() => import("@/pages/VirtualConsultationGuidePage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const BeautyHubPage = lazy(() => import("@/pages/BeautyHubPage")); // "Ask Karine FAQ"
const LipStylesGuidePage = lazy(() => import("@/pages/LipStylesGuidePage"));
const VideoTestPage = lazy(() => import("@/pages/VideoTestPage"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));
const FuturePeekDevPage = lazy(() => import("@/pages/FuturePeekDevPage"));
const KarineBlastDevPage = lazy(() => import("@/pages/KarineBlastDevPage"));
const CardPreviewPage = lazy(() => import("@/pages/CardPreviewPage"));
const EmailPreferencesPage = lazy(() => import("@/pages/EmailPreferencesPage"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const LoyaltyProgramPage = lazy(() => import("@/pages/LoyaltyProgramPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const CrownReservePage = lazy(() => import("@/pages/CrownReservePage"));
const InsiderClubPage = lazy(() => import("@/pages/InsiderClubPage"));
const AdminInsiderPage = lazy(() => import("@/pages/AdminInsiderPage"));
const CrownReservePlanPage = lazy(() => import("@/pages/CrownReservePlanPage"));
const SkinQuizPage = lazy(() => import("@/pages/SkinQuizPage"));
const CrownReserveHubPage = lazy(() => import("@/pages/CrownReserveHubPage"));
const SkinBoostersPage = lazy(() => import("@/pages/SkinBoostersPage"));
const SculptraPage = lazy(() => import("@/pages/SculptraPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const JoinOurTeamPage = lazy(() => import("@/pages/JoinOurTeamPage"));
const PTioxLandingPage = lazy(() => import("@/pages/PTioxLandingPage"));
const CareersPortalPage = lazy(() => import("@/pages/CareersPortalPage"));
const AdminCareersPage = lazy(() => import("@/pages/AdminCareersPage"));
// ProductsPage retired Feb 18 2026 — its 9 products (Colorescience + Latisse)
// are now served from the unified /shop via the store DB. /products now
// redirects to /shop.
const ConsentSignaturePage = lazy(() => import("@/pages/ConsentSignaturePage"));
const ConfirmVisitPage = lazy(() => import("@/pages/ConfirmVisitPage"));
const EminencePage = lazy(() => import("@/pages/EminencePage"));
const AdminEminenceStockPage = lazy(() => import("@/pages/AdminEminenceStockPage"));
const AdminSquareMappingPage = lazy(() => import("@/pages/AdminSquareMappingPage"));
const AdminClientManagementPage = lazy(() => import("@/pages/AdminClientManagementPage"));
const AdminPredictiveMaintenancePage = lazy(() => import("@/pages/AdminPredictiveMaintenancePage"));
const StaffScorecardPage = lazy(() => import("@/pages/StaffScorecardPage"));
const AdminHubPage = lazy(() => import("@/pages/AdminHubPage"));
const AdminAISearchPage = lazy(() => import("@/pages/AdminAISearchPage"));
const AdminFacialAILabPage = lazy(() => import("@/pages/AdminFacialAILabPage"));
const AdminSkinAdvisorAnalyticsPage = lazy(() => import("@/pages/AdminSkinAdvisorAnalyticsPage"));
const CrownGamesLoungePage = lazy(() => import("@/pages/CrownGamesLoungePage"));
const AdminGamesPickPage = lazy(() => import("@/pages/AdminGamesPickPage"));
const AdminClientAliasesPage = lazy(() => import("@/pages/AdminClientAliasesPage"));
const AdminInsiderTipsPage = lazy(() => import("@/pages/AdminInsiderTipsPage"));
const AdminTipVoiceNotesPage = lazy(() => import("@/pages/AdminTipVoiceNotesPage"));
const TipAudioPlayerPage = lazy(() => import("@/pages/TipAudioPlayerPage"));
const DermalFillerPricingPage = lazy(() => import("@/pages/DermalFillerPricingPage"));
const SubscribeToInsiderTipsPage = lazy(() => import("@/pages/SubscribeToInsiderTipsPage"));
const KioskBookNextPage = lazy(() => import("@/pages/KioskBookNextPage"));
const TodaysTipCEFerulicPage = lazy(() => import("@/pages/TodaysTipCEFerulicPage"));
const AdminSpamComplaintsPage = lazy(() => import("@/pages/AdminSpamComplaintsPage"));
const AdminPointsAuditPage = lazy(() => import("@/pages/AdminPointsAuditPage"));
const HeadOfficePage = lazy(() => import("@/pages/HeadOfficePage"));
const CancelAppointmentPage = lazy(() => import("@/pages/CancelAppointmentPage"));
const AdminMembersPage = lazy(() => import("@/pages/AdminMembersPage"));
// Iter 381 · Unified recurring-billing view — pulls Square subscriptions AND
// recurring invoices from all three locations into one admin page.
const AdminRecurringBillingPage = lazy(() => import("@/pages/AdminRecurringBillingPage"));
const AdminMembershipHealthPage = lazy(() => import("@/pages/AdminMembershipHealthPage"));
// Sept 4 2026 · Karine ask · full accounting view (plans · payments · clients)
const AdminMembershipAccountingPage = lazy(() => import("@/pages/AdminMembershipAccountingPage"));
const AdminRewardsActivityPage = lazy(() => import("@/pages/AdminRewardsActivityPage"));
const AdminMessagesPage = lazy(() => import("@/pages/AdminMessagesPage"));
const AdminClientMessagesPage = lazy(() => import("@/pages/AdminClientMessagesPage"));
const StaffLoginPage = lazy(() => import("@/pages/StaffLoginPage"));
const StaffHomePage = lazy(() => import("@/pages/StaffHomePage"));
// Sep 23 2026 · Medical Director Studio (Mushka) — walled-off surface
// where the MD reviews Karine's RN clients and signs directives.
const MDStudioPage = lazy(() => import("@/pages/MDStudioPage"));
const NPJoinPage = lazy(() => import("@/pages/NPJoinPage"));
const BookMenuPage = lazy(() => import("@/pages/BookMenuPage"));
const FreeConsultationPage = lazy(() => import("@/pages/FreeConsultationPage"));
const IconCynosurePage = lazy(() => import("@/pages/IconCynosurePage"));
const PotenzaRfPage = lazy(() => import("@/pages/treatments/PotenzaRfPage"));
const LaserGenesisPage = lazy(() => import("@/pages/treatments/LaserGenesisPage"));
const IplPhotofacialPage = lazy(() => import("@/pages/treatments/IplPhotofacialPage"));
const StaffScanPage = lazy(() => import("@/pages/StaffScanPage"));
const CheckinPage = lazy(() => import("@/pages/CheckinPage"));
const CheckinScanPage = lazy(() => import("@/pages/CheckinScanPage"));
const CheckinQrPosterPage = lazy(() => import("@/pages/CheckinQrPosterPage"));
const CheckinKioskPage = lazy(() => import("@/pages/CheckinKioskPage"));
// Iter 386 · Feb 20 2026 · Reception concierge kiosk hub + 4 sub-flows
const KioskHubPage        = lazy(() => import("@/pages/KioskHubPage"));
const KioskFormsPage      = lazy(() => import("@/pages/KioskFormsPage"));
const KioskRewardsPage    = lazy(() => import("@/pages/KioskRewardsPage"));
const KioskTreatmentsPage = lazy(() => import("@/pages/KioskTreatmentsPage"));
const KioskShopPage       = lazy(() => import("@/pages/KioskShopPage"));
// Iter 387 · Feb 20 2026 · Kiosk Facial AI (biometric-consent gate) + Aftercare rebook wizard
const KioskAiPage         = lazy(() => import("@/pages/KioskAiPage"));
const KioskAftercarePage  = lazy(() => import("@/pages/KioskAftercarePage"));
// Iter 393 · Feb 20 2026 · Phone-only clients self-service points portal
const MyPointsByPhonePage = lazy(() => import("@/pages/MyPointsByPhonePage"));
// Iter 395 · Feb 20 2026 · Studio iPad consultation layout (no-scroll)
const StudioConsultationPage = lazy(() => import("@/pages/StudioConsultationPage"));
const CO2LiftLaunchPage = lazy(() => import("@/pages/studio/CO2LiftLaunchPage"));
// Iter 398 · Feb 20 2026 · Broadcast Center for one-tap client emails
const AdminBroadcastsPage = lazy(() => import("@/pages/AdminBroadcastsPage"));
// Feb 24 2026 · Karine fix — public /deal-of-the-day landing page. The
// weekly-promo email CTA now deep-links here so clients see the exact
// flash-deal price (with regular + savings) before they hit Square.
const DealOfTheDayPage = lazy(() => import("@/pages/DealOfTheDayPage"));
const StaffMyClientsPage = lazy(() => import("@/pages/StaffMyClientsPage"));
const StaffGalleryPage = lazy(() => import("@/pages/StaffGalleryPage"));
const StaffClientDetailPage = lazy(() => import("@/pages/StaffClientDetailPage"));
const StaffAvailabilityPage = lazy(() => import("@/pages/StaffAvailabilityPage"));
// Iter 263 · Feb 2026 · AnnaAdSquare page removed at Karine's ask.
const MyDayPage = lazy(() => import("@/pages/MyDayPage"));
const RfChartingPage = lazy(() => import("@/pages/RfChartingPage"));
const SkinChartingPage = lazy(() => import("@/pages/SkinChartingPage"));
const AdminPromosPage = lazy(() => import("@/pages/AdminPromosPage"));
const AdminLastMinuteClubPage = lazy(() => import("@/pages/AdminLastMinuteClubPage"));
const AdminBookingBlocklistPage = lazy(() => import("@/pages/AdminBookingBlocklistPage"));
const StaffCalendarPage = lazy(() => import("@/pages/StaffCalendarPage"));
const StaffNPCalendarPage = lazy(() => import("@/pages/StaffNPCalendarPage"));
const StaffTodayPage = lazy(() => import("@/pages/StaffTodayPage"));
const StaffFrontDeskBoardPage = lazy(() => import("@/pages/StaffFrontDeskBoardPage"));
const StaffCommissionPage = lazy(() => import("@/pages/StaffCommissionPage"));
const StaffBookabilityAuditPage = lazy(() => import("@/pages/StaffBookabilityAuditPage"));
const StaffTimeClockPage = lazy(() => import("@/pages/StaffTimeClockPage"));
const AdminPayrollPage = lazy(() => import("@/pages/AdminPayrollPage"));
// Aug 2026 · Tanya Shatenko no longer employed — payroll shortcut page
// is dead-code-off. Kept the import commented so re-enabling is a
// one-line uncomment when a replacement is hired.
// const AdminTanyaPayrollPage = lazy(() => import("@/pages/AdminTanyaPayrollPage"));
const AdminPersonalBlocksPage = lazy(() => import("@/pages/AdminPersonalBlocksPage"));
const AdminPayworksPage = lazy(() => import("@/pages/AdminPayworksPage"));
const LaserGenesisLymphaticPromoPage = lazy(() => import("@/pages/LaserGenesisLymphaticPromoPage"));
const GlowResetPromoPage = lazy(() => import("@/pages/GlowResetPromoPage"));
const TwoDSkinRejuvenationPromoPage = lazy(() => import("@/pages/TwoDSkinRejuvenationPromoPage"));
const GlowResetFollowupPage = lazy(() => import("@/pages/GlowResetFollowupPage"));
const KarineLauzonRNAuthorityPage = lazy(() => import("@/pages/KarineLauzonRNAuthorityPage"));
const TanyaJacksonStudioPage = lazy(() => import("@/pages/TanyaJacksonStudioPage"));
const LindaLafranceStudioPage = lazy(() => import("@/pages/LindaLafranceStudioPage"));
const FacialRejuvenationHubPage = lazy(() => import("@/pages/FacialRejuvenationHubPage"));
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const EducationLibraryIndexPage = lazy(() => import("@/pages/learn/EducationLibraryIndexPage"));
const EducationArticlePage = lazy(() => import("@/pages/learn/EducationArticlePage"));
const FrenchLandingWrapper = lazy(() => import("@/components/seo/FrenchLandingWrapper"));
const AdminLobbyTvPage = lazy(() => import("@/pages/AdminLobbyTvPage"));
const AdminTopSpendersPage = lazy(() => import("@/pages/AdminTopSpendersPage"));
const SquarePosCallbackPage = lazy(() => import("@/pages/SquarePosCallbackPage"));
const ConsultationFormPage = lazy(() => import("@/pages/ConsultationFormPage"));
const InjectionChartingPage = lazy(() => import("@/pages/InjectionChartingPage"));
const LaserHairRemovalChartingPage = lazy(() => import("@/pages/LaserHairRemovalChartingPage"));
const LaserHairRemovalPage = lazy(() => import("@/pages/LaserHairRemovalPage"));
const SquareReadinessPage = lazy(() => import("@/pages/SquareReadinessPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const ReferralPage = lazy(() => import("@/pages/ReferralPage"));
const ProfessionalsPage = lazy(() => import("@/pages/ProfessionalsPage"));
const MedicalMicroneedlingPage = lazy(() => import("@/pages/MedicalMicroneedlingPage"));
const BotoxFunnel = lazy(() => import("@/pages/funnels/BotoxFunnel"));
const DermalFillersFunnel = lazy(() => import("@/pages/funnels/DermalFillersFunnel"));
const SkinBoostersFunnel = lazy(() => import("@/pages/funnels/SkinBoostersFunnel"));
const MicroneedlingFunnel = lazy(() => import("@/pages/funnels/MicroneedlingFunnel"));
const SculptraFunnel = lazy(() => import("@/pages/funnels/SculptraFunnel"));
const PromotionsFunnel = lazy(() => import("@/pages/funnels/PromotionsFunnel"));
const SignagePage = lazy(() => import("@/pages/SignagePage"));
// Sept 11 2026 · Karine ask — cinematic auto-rotating deck for Ottawa's
// laser + facial menu. Full-bleed slides with pricing. Runs on lobby TV.
const LobbyLaserFacialsTvPage = lazy(() => import("@/pages/LobbyLaserFacialsTvPage"));
const LobbyInjectablesTvPage  = lazy(() => import("@/pages/LobbyInjectablesTvPage"));
const LobbyWinterTvPage  = lazy(() => import("@/pages/LobbyWinterTvPage"));
const TreatmentCodePage  = lazy(() => import("@/pages/TreatmentCodePage"));
const SignageCinematicPage = lazy(() => import("@/pages/SignageCinematicPage"));
const SignageAnimatedPage = lazy(() => import("@/pages/SignageAnimatedPage"));
const SignageFreshPage = lazy(() => import("@/pages/SignageFreshPage"));
const LobbyHubPage = lazy(() => import("@/pages/LobbyHubPage"));
const MenuPage = lazy(() => import("@/pages/FullStudioMenuPage"));
const HomePagePreview = lazy(() => import("@/pages/HomePagePreview"));
const AdminAIConversationsPage = lazy(() => import("@/pages/AdminAIConversationsPage"));
const AdminAILoadTestPage = lazy(() => import("@/pages/AdminAILoadTestPage"));
const AdminLeadScoringPage = lazy(() => import("@/pages/AdminLeadScoringPage"));
const AdminPaymentPlansPage = lazy(() => import("@/pages/AdminPaymentPlansPage"));
const AdminClientAccountsPage = lazy(() => import("@/pages/AdminClientAccountsPage"));
const HydraFacialPage = lazy(() => import("@/pages/HydraFacialPage"));
const AdminHydraBoostersPage = lazy(() => import("@/pages/AdminHydraBoostersPage"));
const AdminFinancePage = lazy(() => import("@/pages/AdminFinancePage"));
const AdminBookThisLookPage = lazy(() => import("@/pages/AdminBookThisLookPage"));
const PaymentPlanDetailPage = lazy(() => import("@/pages/PaymentPlanDetailPage"));
const CustomPlanLedgerPage = lazy(() => import("@/pages/CustomPlanLedgerPage"));
const DailyTargetsAdminPage = lazy(() => import("@/pages/DailyTargetsAdminPage"));
const CustomerMergeAdminPage = lazy(() => import("@/pages/CustomerMergeAdminPage"));
const AdminFollowUpsPage = lazy(() => import("@/pages/AdminFollowUpsPage"));
const AdminEquipmentPage = lazy(() => import("@/pages/AdminEquipmentPage"));

// Lightweight champagne loader shown only during a route-chunk download
function RouteLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0c0a09]"
      data-testid="route-chunk-loader"
    >
      <div className="h-8 w-8 rounded-full border-2 border-[#d4b896] border-t-transparent animate-spin" />
    </div>
  );
}

// Sept 11 2026 · Karine ask — 301-equivalent redirect that ALSO forwards
// the search string (so retired campaign URLs don't drop ?ref= /
// ?campaign= tracking on the way to their canonical home).
function RedirectPreserveSearch({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search || ""}${hash || ""}`} replace />;
}

// Hoisted out of App() so it is a stable component type — defining it inside App
// remounted every floating widget (Lumi, cart, etc.) on each App re-render.
function PublicChrome({ onOpenChat, onOpenAudio }) {
  const { pathname } = useLocation();
  const isInternal =
    pathname.startsWith("/staff") ||
    pathname.startsWith("/np/join") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/head-office") ||
    pathname.startsWith("/checkin") ||
    pathname.startsWith("/welcome") ||
    pathname.startsWith("/signage") ||
    pathname.startsWith("/tv") ||
    pathname.startsWith("/lobby");
  if (isInternal) return null;
  const isHome = pathname === "/";
  return (
    <>
      {/* Cart + navigation chrome — required on every page */}
      <BookingCart />
      <FloatingCartButton />
      <Breadcrumb />
      <PWAInstallPrompt />

      {/* Global Lumi FAB — visible on every marketing/client page.
          Hidden on /shop (inline box), /admin, /staff, /kiosk, etc. */}
      <LumiFAB />

      {/* Homepage-only floating widgets — keep inner pages clean.
          Re-mount with a soft fade-in when the visitor returns to "/". */}
      {isHome && (
        <div key={`home-chrome-${pathname}`} className="kl-home-chrome contents animate-in fade-in duration-500">
          <FloatingTrending />
          <FloatingPoints />
          <RewardsPrompt />
          {/* FloatingReferral (Refer & Earn / Laser 6+1 / Lumi pill) removed Oct 26 2026 — Karine: "too cluttered". */}
          <PointsToast />
          {/* KarineAudioFloatingWidget removed Jun 23 2026 — bubble crowded the Acne Scar / Crown Reserve area and the Karine voice content is already accessible from the About page and Karine's audio bio in the Crown Reserve card. */}
          {/* LiveChat removed Jun 30 2026 — duplicated the yellow "Ask Karine"
              KarineAIChatBubble below. Karine asked to keep just ONE chat
              surface (the right-side one). LiveChat module is still on disk
              in case any deep-link routes still need it. */}
          {/* KarineAIChatBubble removed Feb 2026 — Karine's ask: "use Lumi
              as the skincare expert and Karine assist and you can replace
              the ask karine pill". Lumi is now the single AI face across
              the site. Booking help is exposed as a quick-action inside
              Lumi's modal (see SkinAdvisorBox `bookingChip`). The
              Karine AI backend + streaming endpoint remain intact in
              case we bring the widget back later. */}
        </div>
      )}
      {/* MobileKarineWidget removed from mobile — Ask Karine now lives in the bottom nav for less clutter */}
    </>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(window.location.pathname === '/');
  const [mobileAudioOpen, setMobileAudioOpen] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  // Scroll-aware fade for floating overlays. Once the user scrolls past
  // ~120px we add `kl-scrolled` to <body>, which dims any element tagged
  // with `.kl-scroll-fade` (Trending widget, Karine Audio bubble, AI Photo
  // Analysis pill, Crown Reserve dock, 1-Minute Skin Tip dock, etc.) so
  // clients can actually read the page. Hovering or focusing them brings
  // them right back to full opacity.
  useEffect(() => {
    const THRESHOLD = 120;
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        const scrolled = window.scrollY > THRESHOLD;
        document.body.classList.toggle("kl-scrolled", scrolled);
        frame = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Karine Feb 2026: pre-warm the Last Minute Club snapshot on ANY page load
  // so the first visitor who clicks "Last Minute Club" — even right after a
  // production redeploy — sees slots instantly instead of the ~6s "warming up"
  // state. Fully fire-and-forget: no await, no state, no UI wait. Safe against
  // stampede on the backend (per-duration lock prevents parallel Square fetches).
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Only prewarm on public site paths — never on staff/admin dashboards.
    const p = window.location.pathname || "";
    if (p.startsWith("/staff") || p.startsWith("/admin")) return;
    // Skip on mobile data-saver mode.
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && conn.saveData) return;
    } catch (_) { /* ignore */ }
    // Fire after paint so we never block LCP / first render.
    const t = setTimeout(() => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/last-minute/slots?duration=30`;
        fetch(url, { method: "GET", keepalive: true }).catch(() => {});
      } catch (_) { /* ignore */ }
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  // Handlers for mobile widget
  const handleOpenMobileChat = useCallback(() => {
    setMobileChatOpen(true);
    // Trigger the LiveChat to open
    window.dispatchEvent(new CustomEvent('open-mobile-chat'));
  }, []);

  const handleOpenMobileAudio = useCallback(() => {
    setMobileAudioOpen(true);
    // Trigger the audio widget to open
    window.dispatchEvent(new CustomEvent('open-mobile-audio'));
  }, []);

  // Keep the global widgets out of staff/admin views — they clutter the workspace.
  // Also scope the noisy floating overlays (Trending promo, Points, Crown Reserve
  // referral badge, Karine audio bubble, Ask-Karine chat, Rewards prompt) to the
  // homepage so they don't cover service-cards / content on inner pages.

  return (
    <HelmetProvider>
    <PageSeoProvider>
    <LanguageProvider>
      <ErrorBoundary>
      <div className="App">
        <CodeProtection />
        <ContentProtection />
        {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
        <div className="grain-overlay" />
        <BrowserRouter>
          <ScrollToTop />
          <DefaultSEO />
          <AISearchTelemetry />
          <FallPromoBanner />
          <WinterPromoBanner />
          <SitewidePromoBanner />
          {/* Floating "Main site" button — rendered globally so every route
              (public, admin, staff, kiosk, lobby, signage) has a one-tap
              path back to the homepage. The component itself decides the
              size + style based on the current route. */}
          <BackToMainButton />
          <CartProvider>
          <ShopCartProvider>
          <PublicChrome
            onOpenChat={handleOpenMobileChat}
            onOpenAudio={handleOpenMobileAudio}
          />
          <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/signage" element={<SignagePage />} />
            <Route path="/signage/cinematic" element={<SignageCinematicPage />} />
            <Route path="/signage/animated" element={<SignageAnimatedPage />} />
            <Route path="/signage/luxe" element={<SignageAnimatedPage />} />
            <Route path="/signage/fresh" element={<SignageFreshPage />} />
            <Route path="/tv" element={<SignagePage />} />
            <Route path="/tv/animated" element={<SignageAnimatedPage />} />
            <Route path="/tv/fresh" element={<SignageFreshPage />} />
            <Route path="/lobby" element={<LobbyHubPage />} />
            <Route path="/lobby/laser-facials-tv" element={<LobbyLaserFacialsTvPage />} />
            <Route path="/lobby/winter-tv" element={<LobbyWinterTvPage />} />
            <Route path="/signage/laser-facials" element={<LobbyLaserFacialsTvPage />} />
            <Route path="/lobby/injectables-tv"  element={<LobbyInjectablesTvPage />} />
            <Route path="/signage/injectables"   element={<LobbyInjectablesTvPage />} />
            <Route path="/lobby-hub" element={<LobbyHubPage />} />
            <Route path="/book" element={<HomePage />} />
            <Route path="/last-minute" element={<HomePage />} />
            <Route path="/lastminute" element={<HomePage />} />
            <Route path="/last-minute-club" element={<LastMinuteClubPage />} />
            <Route path="/last-minute-page" element={<LastMinuteClubPage />} />
            <Route path="/aftercare" element={<AftercarePage />} />
            <Route path="/insider-tips" element={<InsiderTipsBlogPage />} />
            <Route path="/insider-tips/:slug" element={<InsiderTipsBlogDetailPage />} />
            <Route path="/gift-cards" element={<GiftCardsPage />} />
            {/* Eminence retail store — public route, not linked in nav until Karine approves products (Iter 387) */}
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/order-complete" element={<ShopOrderCompletePage />} />
            <Route path="/shop/:sku" element={<ShopProductPage />} />
            {/* Skincare 101 SEO landing pages (Iter 396) */}
            <Route path="/skincare-101" element={<Skincare101Page />} />
            <Route path="/skincare-101/:slug" element={<Skincare101ArticlePage />} />
            {/* French versions (Iter 398 · fr-CA hreflang) */}
            <Route path="/fr/skincare-101" element={<Skincare101Page />} />
            <Route path="/fr/skincare-101/:slug" element={<Skincare101ArticlePage />} />
            <Route path="/financing" element={<FinancingPage />} />
            <Route path="/promotions" element={<PromotionsPage />} />
            {/* Feb 24 2026 — public 72h flash-deal landing (link target for weekly-promo email CTA). */}
            <Route path="/deal-of-the-day" element={<DealOfTheDayPage />} />
            <Route path="/treatments" element={<TreatmentsIndexPage />} />
            <Route path="/np/join/:sid" element={<NPJoinPage />} />
            <Route path="/book/menu" element={<BookMenuPage />} />
            <Route path="/book/free-consultation" element={<FreeConsultationPage />} />
            <Route path="/icon-laser" element={<IconCynosurePage />} />
            <Route path="/potenza-rf-microneedling-ottawa" element={<PotenzaRfPage />} />
            <Route path="/laser-genesis-ottawa" element={<LaserGenesisPage />} />
            <Route path="/ipl-photofacial-ottawa" element={<IplPhotofacialPage />} />
            <Route path="/t/:code" element={<TreatmentCodePage />} />
            <Route path="/treatments/by-provider" element={<TreatmentsByProviderPage />} />
            <Route path="/services" element={<OurServicesPage />} />
            <Route path="/our-services" element={<OurServicesPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/preview/home" element={<HomePagePreview />} />
            <Route path="/lux" element={<HomePagePreview />} />
            <Route path="/preview" element={<HomePagePreview />} />
            {/* Hero V2 mockup — same HomePage shell but renders HeroV2Section
                instead of HeroSection. All other sections (rotating banners,
                Insider Tips, promos, Ask Karine) render untouched. */}
            <Route path="/preview/hero-v2" element={<HomePage />} />
            <Route path="/preview/hero" element={<HomePage />} />
            <Route path="/treatments-by-concern" element={<TreatmentsByConcernPage />} />
            <Route path="/by-concern" element={<TreatmentsByConcernPage />} />
            <Route path="/concerns/:slug" element={<ConcernPage />} />
            <Route path="/afterpay" element={<AfterpayPage />} />
            <Route path="/consent" element={<ConsentFormPage />} />
            <Route path="/consent-form" element={<ConsentFormPage />} />
            {/* Iter 388.15 · Feb 26 2026 · Marc — Kiosk consent bug fix.
                Cached kiosk builds shipped links like /consent/medical_history
                which never had a matching route → blank page. Alias the
                dynamic path to the single generic consent page so old
                buttons work. */}
            <Route path="/consent/:form_key" element={<ConsentFormPage />} />
            <Route path="/checkin" element={<CheckinPage />} />
            <Route path="/check-in" element={<CheckinPage />} />
            <Route path="/checkin/scan/:token" element={<CheckinScanPage />} />
            <Route path="/checkin/qr" element={<CheckinQrPosterPage />} />
            <Route path="/checkin/poster" element={<CheckinQrPosterPage />} />
            <Route path="/checkin/kiosk" element={<CheckinKioskPage />} />
            <Route path="/welcome" element={<CheckinKioskPage />} />
            <Route path="/our-team" element={<OurTeamPage />} />
            <Route path="/providers" element={<OurTeamPage />} />
            <Route path="/team" element={<OurTeamPage />} />
            <Route path="/treatments/aveda-facials" element={<Navigate to="/treatments/eminence-facials" replace />} />
            <Route path="/aveda-facials" element={<Navigate to="/treatments/eminence-facials" replace />} />
            <Route path="/fr/soins-aveda" element={<Navigate to="/treatments/eminence-facials" replace />} />
            <Route path="/treatments/eminence-facials" element={<EminenceFacialsPage />} />
            <Route path="/treatments/eminence-facials-body" element={<Navigate to="/treatments/eminence-facials" replace />} />
            <Route path="/eminence-facials" element={<EminenceFacialsPage />} />
            <Route path="/preview/eminence-launch" element={<EminenceLaunchPreviewPage />} />
            {/* Feb 2026 · Injectables & Fillers portal — Karine ask to make
                every category pill on the /treatments menu land on its
                own jaw-dropping page. */}
            <Route path="/treatments/injectables" element={<InjectablesPage />} />
            <Route path="/injectables" element={<Navigate to="/treatments/injectables" replace />} />
            {/* Feb 2026 · VIP & Memberships portal — mirrors the Éminence
                + Injectables aesthetic (rich brown + forest + ocean). */}
            <Route path="/treatments/vip-memberships" element={<VipMembershipsPage />} />
            <Route path="/vip-memberships" element={<Navigate to="/treatments/vip-memberships" replace />} />
            <Route path="/fr/soins-eminence" element={<FrenchLandingWrapper><EminenceFacialsPage /></FrenchLandingWrapper>} />
            <Route path="/before-after" element={<BeforeAfterGalleryPage />} />
            <Route path="/admin/referrals" element={<AdminAuthGate requireOwner><AdminReferralsPage /></AdminAuthGate>} />
            <Route path="/admin/pro-referrals" element={<AdminAuthGate requireOwner><AdminProReferralsPage /></AdminAuthGate>} />
            <Route path="/admin/professional-referrals" element={<AdminAuthGate requireOwner><AdminProReferralsPage /></AdminAuthGate>} />
            <Route path="/admin/suites" element={<AdminAuthGate requireOwner><AdminSuitesPage /></AdminAuthGate>} />
            <Route path="/admin/add-ons" element={<AdminAuthGate requireOwner><AdminAddOnsPage /></AdminAuthGate>} />
            <Route path="/admin/weekly-promo" element={<AdminAuthGate requireOwner><AdminWeeklyPromoPage /></AdminAuthGate>} />
            <Route path="/admin/spin-winners" element={<AdminAuthGate requireOwner><AdminSpinWinnersPage /></AdminAuthGate>} />
            {/* Iter 200 · Karine's Pick for the Games Lounge */}
            <Route path="/admin/games-pick" element={<AdminAuthGate requireOwner><AdminGamesPickPage /></AdminAuthGate>} />
            <Route path="/admin/spin/catalog" element={<AdminAuthGate requireOwner><AdminSpinCatalogPage /></AdminAuthGate>} />
            <Route path="/admin/spin/settings" element={<AdminAuthGate requireOwner><AdminSpinCatalogPage /></AdminAuthGate>} />
            <Route path="/admin/spin/prize-digest" element={<AdminAuthGate requireOwner><AdminSpinPrizeDigestPage /></AdminAuthGate>} />
            <Route path="/admin/spin/digest" element={<AdminAuthGate requireOwner><AdminSpinPrizeDigestPage /></AdminAuthGate>} />
            <Route path="/staff/spin" element={<AdminAuthGate><StaffSpinRedeemPage /></AdminAuthGate>} />
            <Route path="/staff/online-orders" element={<AdminAuthGate><StaffOnlineOrdersPage /></AdminAuthGate>} />
            <Route path="/admin/fast-track-watchers" element={<AdminAuthGate requireOwner><AdminFastTrackWatchersPage /></AdminAuthGate>} />
            <Route path="/admin/booking-health" element={<AdminAuthGate requireOwner><AdminBookingHealthPage /></AdminAuthGate>} />
            <Route path="/admin/rescue" element={<AdminAuthGate requireOwner><AdminRescueQueuePage /></AdminAuthGate>} />
            <Route path="/admin/rescue-list" element={<AdminAuthGate requireOwner><AdminRescueListPage /></AdminAuthGate>} />
            <Route path="/admin/metrics-studio" element={<AdminAuthGate requireOwner><AdminMetricsStudioPage /></AdminAuthGate>} />
            <Route path="/admin/product-usage" element={<AdminAuthGate><AdminProductUsagePage /></AdminAuthGate>} />
            <Route path="/admin/contractor-pay" element={<AdminAuthGate requireOwner><AdminContractorPayPage /></AdminAuthGate>} />
            <Route path="/admin/np-audit" element={<AdminAuthGate requireOwner><AdminNPAuditPage /></AdminAuthGate>} />
            {/* Iter 292 · Payment Plans — global admin list + detail + staff-only mirror */}
            <Route path="/admin/payment-plans" element={<AdminAuthGate><AdminPaymentPlansPage /></AdminAuthGate>} />
            <Route path="/admin/client-accounts" element={<AdminAuthGate><AdminClientAccountsPage /></AdminAuthGate>} />
            <Route path="/admin/hydrafacial-boosters" element={<AdminAuthGate><AdminHydraBoostersPage /></AdminAuthGate>} />
            <Route path="/admin/finance" element={<AdminFinancePage />} />
            <Route path="/admin/book-this-look" element={<AdminAuthGate><AdminBookThisLookPage /></AdminAuthGate>} />
            <Route path="/head-office/book-this-look" element={<AdminAuthGate><AdminBookThisLookPage /></AdminAuthGate>} />
            <Route path="/admin/payment-plans/:id" element={<AdminAuthGate><PaymentPlanDetailPage /></AdminAuthGate>} />
            <Route path="/staff/payment-plans/:id" element={<AdminAuthGate><PaymentPlanDetailPage isStaffOnly={true} /></AdminAuthGate>} />
            {/* Iter 471 · Sep 19 2026 · Forensic ledger for custom payment plans */}
            <Route path="/admin/custom-plans/:id" element={<AdminAuthGate><CustomPlanLedgerPage /></AdminAuthGate>} />
            <Route path="/admin/daily-targets" element={<AdminAuthGate><DailyTargetsAdminPage /></AdminAuthGate>} />
            <Route path="/admin/customer-merge" element={<AdminAuthGate><CustomerMergeAdminPage /></AdminAuthGate>} />
            <Route path="/staff/custom-plans/:id" element={<AdminAuthGate><CustomPlanLedgerPage /></AdminAuthGate>} />
            <Route path="/admin/loyalty/skip-log" element={<AdminAuthGate requireOwner><AdminLoyaltySkipLogPage /></AdminAuthGate>} />
            <Route path="/referral" element={<ReferralPage />} />
            <Route path="/refer-a-friend" element={<ReferralPage />} />
            <Route path="/refer" element={<ReferralPage />} />
            <Route path="/professionals" element={<ProfessionalsPage />} />
            <Route path="/pro-partner" element={<ProfessionalsPage />} />
            <Route path="/refer-pro" element={<ProfessionalsPage />} />
            <Route path="/share/:code" element={<ShareCodePage />} />
            <Route path="/r/:code" element={<ShareCodePage />} />
            <Route path="/admin/bookings" element={<AdminAuthGate requireOwner><AdminBookingsPage /></AdminAuthGate>} />
            <Route path="/admin/gift-cards" element={<AdminAuthGate requireOwner><AdminGiftCardsPage /></AdminAuthGate>} />
            <Route path="/admin/analytics" element={<AdminAuthGate requireOwner><AnalyticsDashboardPage /></AdminAuthGate>} />
            <Route path="/admin/conversations" element={<AdminAuthGate requireOwner><AdminAIConversationsPage /></AdminAuthGate>} />
            <Route path="/admin/ai-load-test" element={<AdminAuthGate requireOwner><AdminAILoadTestPage /></AdminAuthGate>} />
            <Route path="/admin/lead-scoring" element={<AdminAuthGate requireOwner><AdminLeadScoringPage /></AdminAuthGate>} />
            <Route path="/admin/follow-ups" element={<AdminAuthGate requireOwner><AdminFollowUpsPage /></AdminAuthGate>} />
            <Route path="/admin/equipment" element={<AdminAuthGate requireOwner><AdminEquipmentPage /></AdminAuthGate>} />
            <Route path="/admin/clients" element={<AdminAuthGate requireOwner><AdminClientManagementPage /></AdminAuthGate>} />
            <Route path="/admin/predictive-maintenance" element={<AdminAuthGate requireOwner><AdminPredictiveMaintenancePage /></AdminAuthGate>} />
            <Route path="/admin/staff-scorecard" element={<AdminAuthGate requireOwner><StaffScorecardPage /></AdminAuthGate>} />
            <Route path="/admin/hub" element={<AdminAuthGate requireOwner><AdminHubPage /></AdminAuthGate>} />
            {/* Oct 2 2026 · Fix: bare /admin was blank (no route match).
                Many internal pages Link back to "/admin" — redirect to the
                authenticated Admin Hub so the back-button never dead-ends. */}
            <Route path="/admin" element={<Navigate to="/admin/hub" replace />} />
            {/* Iter 339 · Feb 2026 · AI-Search Visibility Dashboard */}
            <Route path="/admin/ai-search" element={<AdminAuthGate requireOwner><AdminAISearchPage /></AdminAuthGate>} />
            <Route path="/head-office/ai-search" element={<AdminAuthGate requireOwner><AdminAISearchPage /></AdminAuthGate>} />
            {/* Iter 342 · Feb 12 2026 · Facial AI Lab — landmark-based identity-preserving pipeline */}
            <Route path="/admin/facial-ai-lab" element={<AdminAuthGate requireOwner><AdminFacialAILabPage /></AdminAuthGate>} />
            <Route path="/admin/client-aliases" element={<AdminAuthGate requireOwner><AdminClientAliasesPage /></AdminAuthGate>} />
            <Route path="/admin/head-office" element={<AdminAuthGate requireOwner><HeadOfficePage /></AdminAuthGate>} />
            <Route path="/head-office" element={<AdminAuthGate requireOwner><HeadOfficePage /></AdminAuthGate>} />
            <Route path="/admin/members" element={<AdminAuthGate requireOwner><AdminMembersPage /></AdminAuthGate>} />
            <Route path="/admin/recurring" element={<AdminAuthGate requireOwner><AdminRecurringBillingPage /></AdminAuthGate>} />
            <Route path="/admin/recurring-billing" element={<AdminAuthGate requireOwner><AdminRecurringBillingPage /></AdminAuthGate>} />
            <Route path="/admin/membership/health" element={<AdminAuthGate requireOwner><AdminMembershipHealthPage /></AdminAuthGate>} />
            <Route path="/admin/membership-accounting" element={<AdminAuthGate requireOwner><AdminMembershipAccountingPage /></AdminAuthGate>} />
            <Route path="/admin/membership/accounting" element={<AdminAuthGate requireOwner><AdminMembershipAccountingPage /></AdminAuthGate>} />
            <Route path="/admin/accounting" element={<AdminAuthGate requireOwner><AdminMembershipAccountingPage /></AdminAuthGate>} />
            <Route path="/admin/rewards-activity" element={<AdminAuthGate requireOwner><AdminRewardsActivityPage /></AdminAuthGate>} />
            <Route path="/admin/insider-tips-signups" element={<AdminAuthGate requireOwner><AdminInsiderTipsPage /></AdminAuthGate>} />
            <Route path="/admin/newsletter/insider-tips" element={<AdminAuthGate requireOwner><AdminInsiderTipsPage /></AdminAuthGate>} />
            <Route path="/admin/insider-tips-voice" element={<AdminAuthGate requireOwner><AdminTipVoiceNotesPage /></AdminAuthGate>} />
            <Route path="/tip-audio/:tipId" element={<TipAudioPlayerPage />} />
            <Route path="/dermal-filler-pricing" element={<DermalFillerPricingPage />} />
            <Route path="/subscribe" element={<SubscribeToInsiderTipsPage />} />
            <Route path="/kiosk/book-next" element={<KioskBookNextPage />} />
            <Route path="/todays-tip" element={<TodaysTipCEFerulicPage />} />
            <Route path="/skin-tips" element={<SubscribeToInsiderTipsPage />} />
            <Route path="/admin/spam-complaints" element={<AdminAuthGate requireOwner><AdminSpamComplaintsPage /></AdminAuthGate>} />
            <Route path="/admin/square-waitlist-spam" element={<AdminAuthGate requireOwner><AdminSpamComplaintsPage /></AdminAuthGate>} />
            <Route path="/admin/points-audit/:email" element={<AdminAuthGate requireOwner><AdminPointsAuditPage /></AdminAuthGate>} />
            <Route path="/admin/messages" element={<AdminAuthGate requireOwner><AdminMessagesPage /></AdminAuthGate>} />
            <Route path="/admin/client-messages" element={<AdminAuthGate requireOwner><AdminClientMessagesPage /></AdminAuthGate>} />
            <Route path="/staff/login" element={<StaffLoginPage />} />
            {/* Sep 23 2026 · Nurse Practitioner Studio (Mushka) — /md/*
                and /np/* are both walled off from admin/staff surfaces
                (no sales, points, plans, revenue). Guard: role must be
                medical_director or owner. Sep 25 2026 — /np/studio added
                as the CNO-compliant alias per new March-2027 language. */}
            <Route path="/md" element={<MDAuthGate><MDStudioPage /></MDAuthGate>} />
            <Route path="/md/studio" element={<MDAuthGate><MDStudioPage /></MDAuthGate>} />
            <Route path="/np" element={<MDAuthGate><MDStudioPage /></MDAuthGate>} />
            <Route path="/np/studio" element={<MDAuthGate><MDStudioPage /></MDAuthGate>} />
            {/* Oct 2 2026 · Fix: bare /studio was blank (no route match).
                Route to the NP/MD Studio — gate decides where the signed-in
                staff member actually lands. */}
            <Route path="/studio" element={<MDAuthGate><MDStudioPage /></MDAuthGate>} />
            {/* /staff/scan is intentionally NOT wrapped in AdminAuthGate.
                The `t=<jwt>` query param is proof of staff intent — it's
                signed with our JWT_SECRET and minted only by backend SMS
                handlers (see checkin_module.py#_staff_deep_link). This lets
                Karine/Jenny tap the SMS notification and land DIRECTLY on
                the client's mini-dashboard without a login interstitial. */}
            <Route path="/staff/scan" element={<StaffScanPage />} />
            <Route path="/staff" element={<AdminAuthGate><StaffHomePage /></AdminAuthGate>} />
            <Route path="/staff/clients" element={<AdminAuthGate><StaffMyClientsPage /></AdminAuthGate>} />
            <Route path="/staff/clients/:email" element={<AdminAuthGate><StaffClientDetailPage /></AdminAuthGate>} />
            <Route path="/staff/my-pay" element={<AdminAuthGate><StaffMyPayPage /></AdminAuthGate>} />
            <Route path="/staff/gallery" element={<AdminAuthGate><StaffGalleryPage /></AdminAuthGate>} />
            <Route path="/staff/my-day" element={<AdminAuthGate><MyDayPage /></AdminAuthGate>} />
            <Route path="/staff/rf-charting" element={<AdminAuthGate><RfChartingPage /></AdminAuthGate>} />
            <Route path="/staff/skin-charting" element={<AdminAuthGate><SkinChartingPage /></AdminAuthGate>} />
            <Route path="/admin/promos" element={<AdminAuthGate requireOwner><AdminPromosPage /></AdminAuthGate>} />
            <Route path="/admin/last-minute-club" element={<AdminAuthGate requireOwner><AdminLastMinuteClubPage /></AdminAuthGate>} />
            <Route path="/admin/booking-blocklist" element={<AdminAuthGate requireOwner><AdminBookingBlocklistPage /></AdminAuthGate>} />
            {/* Public self-service appointment cancel — no portal login
                required. Linked from confirmation emails. Validates email
                matches Square's customer record before cancelling. */}
            <Route path="/cancel-appointment" element={<CancelAppointmentPage />} />
            <Route path="/staff/calendar" element={<AdminAuthGate><StaffTodayPage /></AdminAuthGate>} />
            <Route path="/staff/np-calendar" element={<AdminAuthGate><StaffNPCalendarPage /></AdminAuthGate>} />
            <Route path="/staff/calendar/legacy" element={<AdminAuthGate><StaffCalendarPage /></AdminAuthGate>} />
            <Route path="/staff/today" element={<AdminAuthGate><StaffTodayPage /></AdminAuthGate>} />
            <Route path="/staff/board" element={<AdminAuthGate><StaffFrontDeskBoardPage /></AdminAuthGate>} />
            {/* Iter 446 · Sep 15 2026 · Karine ask — provider-locked staff
                entry points. `/staff/tanya` opens the same command
                centre pre-filtered to Tanya's calendar (Ottawa Mon+Fri).
                All chart/consent/photo/client panels work identically.
                `/staff/karine` mirrors the pattern so Karine can bookmark
                her own view without picking "Karine" from the filter every
                morning. */}
            <Route path="/staff/tanya" element={<AdminAuthGate><StaffTodayPage /></AdminAuthGate>} />
            <Route path="/staff/karine" element={<AdminAuthGate><StaffTodayPage /></AdminAuthGate>} />
            <Route path="/staff/linda" element={<AdminAuthGate><StaffTodayPage /></AdminAuthGate>} />
            {/* Iter 473 · Sep 19 2026 · Karine ask — commission ledger */}
            <Route path="/staff/commission" element={<AdminAuthGate><StaffCommissionPage /></AdminAuthGate>} />
            <Route path="/admin/commission"  element={<AdminAuthGate requireOwner><StaffCommissionPage /></AdminAuthGate>} />
            <Route path="/staff/bookability-audit" element={<AdminAuthGate><StaffBookabilityAuditPage /></AdminAuthGate>} />
            <Route path="/staff/time-clock" element={<StaffTimeClockPage />} />
            <Route path="/admin/payroll" element={<AdminAuthGate requireOwner><AdminPayrollPage /></AdminAuthGate>} />
            {/* Aug 2026 · Tanya Shatenko no longer employed — dedicated
                shortcut route removed. Framework intact for future
                aesthetician: uncomment below + re-add the lazy import
                on line 203 when the new hire's payroll goes live. */}
            {/* <Route path="/admin/payroll/tanya" element={<AdminAuthGate requireOwner><AdminTanyaPayrollPage /></AdminAuthGate>} /> */}
            <Route path="/admin/personal-blocks" element={<AdminAuthGate requireOwner><AdminPersonalBlocksPage /></AdminAuthGate>} />
            <Route path="/admin/payworks" element={<AdminAuthGate requireOwner><AdminPayworksPage /></AdminAuthGate>} />
            {/* Sept 11 2026 · Karine ask · retire the season-specific
                URL. Canonical is now /promo/laser-genesis-lymphatic.
                The `-august` alias is kept as a client-side 301-equivalent
                redirect (React Router only ships a 200 HTML shell, but
                Navigate replace preserves history semantics — closest
                we can get without edge-server rewrites). The wrapper
                below also forwards the ?query string so email + IG
                campaign tracking params survive the redirect. Sitemap
                now advertises only the clean URL, so search engines
                will consolidate ranking. */}
            <Route path="/promo/laser-genesis-lymphatic-august" element={<RedirectPreserveSearch to="/promo/laser-genesis-lymphatic" />} />
            {/* Oct 7 2026 · Cheryl FB-ad promo short URL → promotions page
                with hash-anchor so the Fall Lymphatic+HydraFacial hero
                card scrolls into view. Separate alias /promo/fall-149
                because that's how Marc tags the FB UTM. */}
            <Route path="/promo/lymphatic-hydrafacial" element={<RedirectPreserveSearch to="/promotions#fall-lymphatic-hydrafacial" />} />
            <Route path="/promo/fall-149" element={<RedirectPreserveSearch to="/promotions#fall-lymphatic-hydrafacial" />} />
            {/* Iter 344 · Feb 12 2026 · GLOW RESET promo (Redensity 1 + HydraFacial) */}
            <Route path="/promo/glow-reset" element={<GlowResetPromoPage />} />
            <Route path="/glow-reset" element={<GlowResetPromoPage />} />
            {/* Iter 345 · Feb 12 2026 · One-tap HydraFacial follow-up landing */}
            <Route path="/glow-reset/followup" element={<GlowResetFollowupPage />} />
            <Route path="/promo/laser-genesis-lymphatic" element={<LaserGenesisLymphaticPromoPage />} />
            <Route path="/promo/lg-lymph" element={<LaserGenesisLymphaticPromoPage />} />
            {/* Sep 27 2026 · NEW 2D/3D Power Combo signature service */}
            <Route path="/2d-skin-rejuvenation" element={<TwoDSkinRejuvenationPromoPage />} />
            <Route path="/3d-skin-rejuvenation" element={<TwoDSkinRejuvenationPromoPage />} />
            <Route path="/promo/2d-skin-rejuvenation" element={<TwoDSkinRejuvenationPromoPage />} />
            <Route path="/karine-lauzon-rn" element={<KarineLauzonRNAuthorityPage />} />
            <Route path="/karine-lauzon" element={<KarineLauzonRNAuthorityPage />} />
            {/* Iter 397 · E-E-A-T author alias for Skincare 101 articles */}
            <Route path="/about/karine-lauzon-rn" element={<KarineLauzonRNAuthorityPage />} />
            <Route path="/about/karine" element={<KarineLauzonRNAuthorityPage />} />
            {/* Iter 445 · Tanya Jackson studio page — Medical Aesthetician
                landing hub. Multiple aliases so /tanya works too. */}
            <Route path="/tanya-jackson-studio" element={<TanyaJacksonStudioPage />} />
            <Route path="/tanya-jackson" element={<TanyaJacksonStudioPage />} />
            <Route path="/tanya" element={<TanyaJacksonStudioPage />} />
            <Route path="/about/tanya-jackson" element={<TanyaJacksonStudioPage />} />
            {/* Iter 468 · Sep 17 2026 · Linda Lafrance NP studio page —
                mirror of Tanya's studio layout, adapted for Linda's NP /
                CBAM medical-aesthetic menu (Ottawa Thursdays only). */}
            <Route path="/linda-lafrance-studio" element={<LindaLafranceStudioPage />} />
            <Route path="/linda-lafrance" element={<LindaLafranceStudioPage />} />
            <Route path="/linda" element={<LindaLafranceStudioPage />} />
            <Route path="/about/linda-lafrance" element={<LindaLafranceStudioPage />} />
            <Route path="/facial-rejuvenation-ottawa" element={<FacialRejuvenationHubPage />} />
            <Route path="/facial-rejuvenation" element={<FacialRejuvenationHubPage />} />
            <Route path="/locations/ottawa" element={<LocationPage slug="ottawa" />} />
            <Route path="/locations/kanata" element={<LocationPage slug="kanata" />} />
            <Route path="/locations/carleton-place" element={<LocationPage slug="carleton-place" />} />
            {/* Iter 339 · Feb 2026 · Education Library (Level 2 SEO — not in nav) */}
            <Route path="/learn" element={<EducationLibraryIndexPage />} />
            <Route path="/learn/:slug" element={<EducationArticlePage />} />
            {/* Iter 341 · Feb 12 2026 · French twins for hreflang SEO.
                Same components; FrenchLandingWrapper flips the language
                context to "fr" on mount so the built-in FR strings render. */}
            <Route path="/fr/karine-lauzon-rn" element={<FrenchLandingWrapper><KarineLauzonRNAuthorityPage /></FrenchLandingWrapper>} />
            <Route path="/fr/botox-ottawa" element={<FrenchLandingWrapper><BotoxOttawaPage /></FrenchLandingWrapper>} />
            <Route path="/fr/dermal-filler-ottawa" element={<FrenchLandingWrapper><DermalFillerOttawaPage /></FrenchLandingWrapper>} />
            <Route path="/fr/sculptra-ottawa" element={<FrenchLandingWrapper><SculptraOttawaPage /></FrenchLandingWrapper>} />
            <Route path="/fr/locations/ottawa" element={<FrenchLandingWrapper><LocationPage slug="ottawa" /></FrenchLandingWrapper>} />
            <Route path="/fr/locations/kanata" element={<FrenchLandingWrapper><LocationPage slug="kanata" /></FrenchLandingWrapper>} />
            <Route path="/fr/locations/carleton-place" element={<FrenchLandingWrapper><LocationPage slug="carleton-place" /></FrenchLandingWrapper>} />
            <Route path="/admin/lobby-tv" element={<AdminAuthGate requireOwner><AdminLobbyTvPage /></AdminAuthGate>} />
            <Route path="/admin/skin-advisor-analytics" element={<AdminAuthGate requireOwner><AdminSkinAdvisorAnalyticsPage /></AdminAuthGate>} />
            <Route path="/admin/top-spenders" element={<AdminAuthGate requireOwner><AdminTopSpendersPage /></AdminAuthGate>} />
            <Route path="/staff/square-pos-callback" element={<SquarePosCallbackPage />} />
            <Route path="/consultation/:token" element={<ConsultationFormPage />} />
            <Route path="/staff/injection-charting" element={<AdminAuthGate><InjectionChartingPage /></AdminAuthGate>} />
            <Route path="/staff/laser-hair-removal-charting" element={<AdminAuthGate><LaserHairRemovalChartingPage /></AdminAuthGate>} />
            <Route path="/staff/square-readiness" element={<AdminAuthGate><SquareReadinessPage /></AdminAuthGate>} />
            <Route path="/staff/availability" element={<AdminAuthGate><StaffAvailabilityPage /></AdminAuthGate>} />
            {/* Iter 263 · /ads/tanya route retired */}
            <Route path="/admin/lmc" element={<AdminAuthGate requireOwner><AdminLastMinuteClubPage /></AdminAuthGate>} />
            <Route path="/admin/subscriptions" element={<AdminAuthGate requireOwner><AdminSubscriptionsPage /></AdminAuthGate>} />
            <Route path="/admin/store" element={<AdminAuthGate requireOwner><AdminStorePage /></AdminAuthGate>} />
            <Route path="/admin/shop" element={<AdminAuthGate requireOwner><AdminStorePage /></AdminAuthGate>} />
            <Route path="/admin/consultations" element={<AdminAuthGate requireOwner><AdminConsultationHistoryPage /></AdminAuthGate>} />
            <Route path="/botox-ottawa" element={<BotoxOttawaPage />} />
            <Route path="/combos" element={<CombosPage />} />
            <Route path="/lift-restore-program" element={<LiftRestoreProgramPage />} />
            <Route path="/rebuilding-programs" element={<LiftRestoreProgramPage />} />
            <Route path="/menopause-program" element={<MenopauseProgramPage />} />
            <Route path="/menopause-rebuilding" element={<MenopauseProgramPage />} />
            <Route path="/andropause-program" element={<AndropauseProgramPage />} />
            <Route path="/andropause" element={<AndropauseProgramPage />} />
            <Route path="/mens-restore" element={<AndropauseProgramPage />} />
            <Route path="/prevention" element={<PreventionPage />} />
            <Route path="/age-well" element={<PreventionPage />} />
            <Route path="/terms-of-use" element={<TermsOfUsePage />} />
            <Route path="/terms" element={<TermsOfUsePage />} />
            <Route path="/lip-filler-ottawa" element={<LipFillerOttawaPage />} />
            <Route path="/microneedling-ottawa" element={<MicroneedlingOttawaPage />} />
            <Route path="/medical-microneedling" element={<MedicalMicroneedlingPage />} />
            <Route path="/microneedling" element={<MedicalMicroneedlingPage />} />
            {/* Mini Funnel Pages */}
            <Route path="/get/botox" element={<BotoxFunnel />} />
            <Route path="/get/fillers" element={<DermalFillersFunnel />} />
            <Route path="/get/skin-boosters" element={<SkinBoostersFunnel />} />
            <Route path="/get/microneedling" element={<MicroneedlingFunnel />} />
            <Route path="/get/sculptra" element={<SculptraFunnel />} />
            <Route path="/get/promotions" element={<PromotionsFunnel />} />
            <Route path="/dermal-filler-ottawa" element={<DermalFillerOttawaPage />} />
            <Route path="/dermal-fillers" element={<DermalFillersPage />} />
            <Route path="/fillers" element={<DermalFillersPage />} />
            {/* Radiesse® — Merz CaHA biostimulator (Sept 2026 · Karine ask) */}
            <Route path="/radiesse" element={<RadiessePage />} />
            <Route path="/treatments/radiesse" element={<RadiessePage />} />
            <Route path="/radiesse-ottawa" element={<RadiessePage />} />
            <Route path="/radiesse-hands" element={<RadiesseHandsPage />} />
            <Route path="/fr/radiesse-hands" element={<RadiesseHandsPage />} />
            <Route path="/radiesse-neck-decollete" element={<RadiesseNeckDecolletePage />} />
            <Route path="/fr/radiesse-neck-decollete" element={<RadiesseNeckDecolletePage />} />
            {/* Sept 4 2026 · Treatment map — one page, every service, every price */}
            <Route path="/treatments/all" element={<TreatmentMapPage />} />
            <Route path="/all-treatments" element={<TreatmentMapPage />} />
            <Route path="/menu" element={<TreatmentMapPage />} />
            <Route path="/treatment-map" element={<TreatmentMapPage />} />
            <Route path="/services" element={<TreatmentMapPage />} />
            {/* Sept 4 2026 · Public prize-redemption landing (Patricia
                Russell bug — Spin & Win winners couldn't reach a page
                that explained how to book their prize). */}
            <Route path="/spin-and-win" element={<SpinRedeemLandingPage />} />
            <Route path="/redeem" element={<SpinRedeemLandingPage />} />
            <Route path="/my-prize" element={<SpinRedeemLandingPage />} />
            <Route path="/prize" element={<SpinRedeemLandingPage />} />
            {/* Hidden preview route for the new area-based pricing overhaul.
                NOT linked in Navbar/menus. Awaiting Karine's approval to go live. */}
            <Route path="/fillers-v2-preview" element={<DermalFillersAreaPricingPage />} />
            <Route path="/dermal-fillers-new" element={<DermalFillersAreaPricingPage />} />
            {/* Facial AI — identity-locked facial visualisation (preview) */}
            <Route path="/facial-ai" element={<SeeYourFuturePage />} />
            <Route path="/see-your-future" element={<SeeYourFuturePage />} />
            <Route path="/facial-vision-ai" element={<SeeYourFuturePage />} />
            {/* Laser & Crown Skin Treatments hub + SEO-friendly aliases */}
            <Route path="/laser-and-skin" element={<LaserAndSkinPage />} />
            <Route path="/laser-hair-removal" element={<LaserHairRemovalPage />} />
            <Route path="/laser-249" element={<Navigate to={LASER_249_BOOK_URL} replace />} />
            <Route path="/249" element={<Navigate to={LASER_249_BOOK_URL} replace />} />
            <Route path="/laser-unlimited" element={<Navigate to={LASER_249_BOOK_URL} replace />} />
            <Route path="/laser-hair-removal-ottawa" element={<LaserHairRemovalPage />} />
            {/* Oct 31 2026 · SEO audit — laser is Ottawa-only; the Kanata /
                Carleton Place laser URLs were thin duplicates of the Laser &
                Skin page (doorway risk). Redirect to the real page. */}
            <Route path="/laser-hair-removal-kanata" element={<Navigate to="/laser-hair-removal" replace />} />
            <Route path="/laser-hair-removal-carleton-place" element={<Navigate to="/laser-hair-removal" replace />} />
            <Route path="/crown-laser" element={<LaserAndSkinPage />} />
            <Route path="/hydrafacial" element={<HydraFacialPage />} />
            <Route path="/hydrafacial-ottawa" element={<Navigate to="/hydrafacial" replace />} />
            {/* Feb 2026 · Karine ask — "no other laser pages should
                exist in any link but the new one." These legacy
                /mens-laser + /womens-laser routes now redirect to the
                dedicated /laser-hair-removal page. */}
            <Route path="/mens-laser" element={<Navigate to="/laser-hair-removal" replace />} />
            <Route path="/womens-laser" element={<Navigate to="/laser-hair-removal" replace />} />
            {/* Tanya's Lash & Brow Studio (sub-tab under Laser & Skin) */}
            <Route path="/lash-brow-studio" element={<LashBrowStudioPage />} />
            <Route path="/chemical-peels" element={<ChemicalPeelsPage />} />
            <Route path="/chemical-peels-ottawa" element={<ChemicalPeelsPage />} />
            <Route path="/brow-lamination-ottawa" element={<LashBrowStudioPage />} />
            <Route path="/lash-lift-ottawa" element={<LashBrowStudioPage />} />
            {/* Internal preview — luxury navbar mockups for Karine */}
            <Route path="/nav-mockups" element={<NavMockupsPage />} />
            <Route path="/prp-ottawa" element={<PRPOttawaPage />} />
            <Route path="/sculptra-ottawa" element={<SculptraOttawaPage />} />
            {/* Iter 293e · SEO landing pages — Nefertiti Lift + Redensity 1 skin booster */}
            <Route path="/nefertiti-lift-ottawa" element={<NefertitiLiftOttawaPage />} />
            <Route path="/nefertiti-lift" element={<NefertitiLiftOttawaPage />} />
            <Route path="/redensity-1-ottawa" element={<RedensityOttawaPage />} />
            <Route path="/co2lift-ottawa" element={<CO2LiftOttawaPage />} />
            <Route path="/carboxytherapy-ottawa" element={<CO2LiftOttawaPage />} />
            <Route path="/co2-lift" element={<CO2LiftOttawaPage />} />
            <Route path="/latisse-ottawa" element={<LatisseOttawaPage />} />
            <Route path="/latisse" element={<LatisseOttawaPage />} />
            <Route path="/eyelash-serum-ottawa" element={<LatisseOttawaPage />} />
            <Route path="/lash-growth-ottawa" element={<LatisseOttawaPage />} />
            <Route path="/redensity" element={<RedensityOttawaPage />} />
            <Route path="/skin-booster-ottawa" element={<RedensityOttawaPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/virtual-consultation-guide" element={<VirtualConsultationGuidePage />} />
            <Route path="/virtual-faq" element={<VirtualConsultationFAQ />} />
            <Route path="/video-test" element={<VideoTestPage />} />
            <Route path="/test-camera" element={<VideoTestPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/aesthetic-guide" element={<BeautyHubPage />} />
            <Route path="/beauty-hub" element={<BeautyHubPage />} /> {/* Redirect old URL */}
            <Route path="/aesthetic-guide/lip-styles" element={<LipStylesGuidePage />} />
            <Route path="/beauty-hub/lip-styles" element={<LipStylesGuidePage />} /> {/* Redirect old URL */}
            <Route path="/blog/:blogId" element={<BlogArticlePage />} />
            <Route path="/my-appointments" element={<ClientPortalPage />} />
            <Route path="/account" element={<ClientPortalPage />} />
            <Route path="/my-account" element={<ClientPortalPage />} />
            <Route path="/client-portal" element={<ClientPortalPage />} />
            <Route path="/portal" element={<ClientPortalPage />} />
            <Route path="/dev/future-peek" element={<FuturePeekDevPage />} />
            <Route path="/dev/karine-blast" element={<KarineBlastDevPage />} />
            <Route path="/card-preview" element={<CardPreviewPage />} />
            <Route path="/email-preferences" element={<EmailPreferencesPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/rewards" element={<CrownReservePage />} />
            <Route path="/crown-reserve" element={<CrownReservePage />} />
            <Route path="/insider" element={<InsiderClubPage />} />
            <Route path="/admin/insider" element={<AdminInsiderPage />} />
            <Route path="/crown-reserve-plan" element={<CrownReservePlanPage />} />
            <Route path="/skin-quiz" element={<SkinQuizPage />} />
            <Route path="/crown-reserve-hub" element={<CrownReserveHubPage />} />
            <Route path="/skin-boosters" element={<SkinBoostersPage />} />
            <Route path="/sculptra" element={<SculptraPage />} />
            {/* Feb 18 2026 · Iter 400 — legacy ProductsPage (Colorescience + Latisse)
                was orphaned when /shop was reassigned to the new Eminence ShopPage.
                Its 9 products are now merged into the unified store via
                seed_legacy_products.py, so /products just redirects to /shop. */}
            <Route path="/products" element={<Navigate to="/shop" replace />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/fr/carrieres" element={<CareersPage />} />
            <Route path="/join-our-team" element={<JoinOurTeamPage />} />
            <Route path="/fr/joignez-notre-equipe" element={<JoinOurTeamPage />} />
            <Route path="/skincare/p-tiox" element={<PTioxLandingPage />} />
            <Route path="/fr/soins/p-tiox" element={<PTioxLandingPage />} />
            <Route path="/careers/portal" element={<CareersPortalPage />} />
            <Route path="/fr/carrieres/portail" element={<CareersPortalPage />} />
            <Route path="/admin/careers" element={<AdminAuthGate requireOwner><AdminCareersPage /></AdminAuthGate>} />
            <Route path="/consent-sign" element={<ConsentSignaturePage />} />
            <Route path="/kiosk/consent" element={<ConsentSignaturePage />} />
            {/* Reception concierge kiosk (Iter 386 · Feb 20 2026) — 6-tile hub + 4 sub-flows */}
            <Route path="/kiosk"            element={<KioskHubPage />} />
            <Route path="/kiosk/forms"      element={<KioskFormsPage />} />
            <Route path="/kiosk/rewards"    element={<KioskRewardsPage />} />
            <Route path="/kiosk/treatments" element={<KioskTreatmentsPage />} />
            <Route path="/kiosk/shop"       element={<KioskShopPage />} />
            <Route path="/kiosk/ai"         element={<KioskAiPage />} />
            <Route path="/kiosk/aftercare"  element={<KioskAftercarePage />} />
            <Route path="/my-points"        element={<MyPointsByPhonePage />} />
            <Route path="/my-points/:phone" element={<MyPointsByPhonePage />} />
            <Route path="/studio/consultation" element={<StudioConsultationPage />} />
            <Route path="/studio/consultation/:email" element={<StudioConsultationPage />} />
            <Route path="/studio/launch/co2lift" element={<MDAuthGate><CO2LiftLaunchPage /></MDAuthGate>} />
            <Route path="/admin/broadcasts" element={<AdminBroadcastsPage />} />
            <Route path="/confirm-visit/:booking_id" element={<ConfirmVisitPage />} />
            <Route path="/shop/eminence" element={<EminencePage />} />
            <Route path="/eminence" element={<EminencePage />} />
            <Route path="/admin/eminence-stock" element={<AdminEminenceStockPage />} />
            <Route path="/admin/square-mapping" element={<AdminSquareMappingPage />} />
            <Route path="/rewards" element={<LoyaltyProgramPage />} />
            {/* Iter 196 · Jul 30 2026 — Crown Games Lounge (waiting-room entertainment) */}
            <Route path="/lounge" element={<CrownGamesLoungePage />} />
            <Route path="/lounge/:gameId" element={<CrownGamesLoungePage />} />
            {/* Oct 27 2026 · link audit — retired/mistyped paths → canonical pages */}
            <Route path="/before-after-gallery" element={<RedirectPreserveSearch to="/before-after" />} />
            <Route path="/loyalty-program" element={<RedirectPreserveSearch to="/rewards" />} />
            <Route path="/prior-care" element={<RedirectPreserveSearch to="/aftercare" />} />
            <Route path="/programs/menopause" element={<RedirectPreserveSearch to="/menopause-program" />} />
            <Route path="/programs/andropause" element={<RedirectPreserveSearch to="/andropause-program" />} />
            <Route path="/prp-treatments" element={<RedirectPreserveSearch to="/prp-ottawa" />} />
            <Route path="/treatments/dermal-fillers" element={<RedirectPreserveSearch to="/dermal-fillers" />} />
            <Route path="/treatments/laser-and-skin" element={<RedirectPreserveSearch to="/laser-and-skin" />} />
            <Route path="/waitlist" element={<RedirectPreserveSearch to="/last-minute-club" />} />
            <Route path="/cart" element={<RedirectPreserveSearch to="/book" />} />
            <Route path="/admin/custom-plans" element={<RedirectPreserveSearch to="/admin/payment-plans" />} />
            <Route path="/staff/scorecard" element={<RedirectPreserveSearch to="/admin/staff-scorecard" />} />
            <Route path="/staff/consent" element={<RedirectPreserveSearch to="/consent-form" />} />
            <Route path="/staff/inventory" element={<RedirectPreserveSearch to="/admin/eminence-stock" />} />
            <Route path="/staff/settings" element={<RedirectPreserveSearch to="/staff/today" />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </ShopCartProvider>
          </CartProvider>
        </BrowserRouter>
        <FrenchSuggestionBanner />
        <ContactModal />
        {/* Iter 172 — moved from top-right to bottom-right. The staff
            "Welcome back" toast was landing on top of the SetupPasskeyPrompt
            banner's "Set up Face ID" button on iPad viewports, swallowing
            the tap (Karine's Face ID enroll bug). Bottom-right keeps toasts
            visible without overlapping any top-of-page action banner. */}
        <Toaster position="bottom-right" richColors mobileOffset={{ bottom: "88px" }} />
        {/* Iter 387 · Full-screen ping overlay + bell chord. Renders nothing
            until a `provider_ping` SSE event arrives from the backend, at
            which point Karine / Tanya / any staffer sees the client's name
            flash across their screen with sound. */}
        <StaffPingAlert />
        {/* Iter 250 · iPad Version Badge — tiny bottom-left corner
            label showing the running JS build hash so a stale iPad
            cache after deploy is spotted in one glance. */}
        <VersionBadge />
        <PersistentLocationPill />
        <NotSureWizard />
      </div>
      </ErrorBoundary>
    </LanguageProvider>
    </PageSeoProvider>
    </HelmetProvider>
  );
}

export default App;
